// In-app replacement for window.confirm / window.prompt / window.alert.
//
// Native dialogs were the single biggest source of "the editor throws weird
// browser popups": they render as chrome the product does not control, they
// block the main thread, they cannot show product copy or a busy state, and in
// an iframe-heavy editor they read as a browser malfunction rather than as the
// app asking a question. Every call site now awaits one of these instead.

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";

export type DialogTone = "default" | "danger";

export type ConfirmOptions = {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: DialogTone;
};

export type PromptOptions = ConfirmOptions & {
  label: string;
  defaultValue?: string;
  placeholder?: string;
  maxLength?: number;
  requiredValue?: string;
};

export type NotifyOptions = {
  title: string;
  body?: string;
  tone?: DialogTone;
  dismissLabel?: string;
};

type Request =
  | { kind: "confirm"; options: ConfirmOptions; resolve: (value: boolean) => void }
  | { kind: "prompt"; options: PromptOptions; resolve: (value: string | null) => void }
  | { kind: "notify"; options: NotifyOptions; resolve: (value: void) => void };

type DialogApi = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  promptText: (options: PromptOptions) => Promise<string | null>;
  notify: (options: NotifyOptions) => Promise<void>;
};

const DialogContext = createContext<DialogApi | null>(null);

export function useAppDialog(): DialogApi {
  const api = useContext(DialogContext);
  if (!api) throw new Error("useAppDialog must be used inside <AppDialogProvider>");
  return api;
}

export function AppDialogProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<Request | null>(null);
  const [value, setValue] = useState("");
  // Resolving through a ref keeps "closed exactly once" true even if a render
  // races the click: an unresolved promise here would hang the caller forever.
  const pending = useRef<Request | null>(null);

  const open = useCallback((next: Request, initialValue = "") => {
    // A second ask while one is open cancels the first rather than stranding it.
    settle(pending.current, null);
    pending.current = next;
    setValue(initialValue);
    setRequest(next);
  }, []);

  const close = useCallback((accepted: boolean, text: string) => {
    const current = pending.current;
    pending.current = null;
    setRequest(null);
    setValue("");
    settle(current, accepted ? text : null);
  }, []);

  const api = useMemo<DialogApi>(() => ({
    confirm: (options) => new Promise<boolean>((resolve) => {
      open({ kind: "confirm", options, resolve });
    }),
    promptText: (options) => new Promise<string | null>((resolve) => {
      open({ kind: "prompt", options, resolve }, options.defaultValue ?? "");
    }),
    notify: (options) => new Promise<void>((resolve) => {
      open({ kind: "notify", options, resolve });
    }),
  }), [open]);

  return (
    <DialogContext.Provider value={api}>
      {children}
      {request && <DialogSurface request={request} value={value} onValue={setValue} onClose={close} />}
    </DialogContext.Provider>
  );
}

function settle(request: Request | null, text: string | null) {
  if (!request) return;
  if (request.kind === "confirm") request.resolve(text !== null);
  else if (request.kind === "prompt") request.resolve(text);
  else request.resolve();
}

function DialogSurface({
  request, value, onValue, onClose,
}: {
  request: Request;
  value: string;
  onValue: (value: string) => void;
  onClose: (accepted: boolean, value: string) => void;
}) {
  const { options } = request;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const tone = options.tone ?? "default";
  const isNotify = request.kind === "notify";
  const canConfirm = request.kind !== "prompt" || request.options.requiredValue === undefined || value === request.options.requiredValue;

  useEffect(() => {
    (inputRef.current ?? confirmRef.current)?.focus();
  }, [request]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClose(isNotify, value);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [isNotify, onClose, value]);

  return (
    <div className="app-dialog-backdrop" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose(isNotify, value);
    }}>
      <div className={`app-dialog tone-${tone}`} role="alertdialog" aria-modal="true" aria-labelledby="app-dialog-title">
        <h2 id="app-dialog-title">{options.title}</h2>
        {options.body && <p>{options.body}</p>}
        {request.kind === "prompt" && (
          <label className="app-dialog-field">
            <span>{request.options.label}</span>
            <input
              ref={inputRef}
              value={value}
              placeholder={request.options.placeholder}
              maxLength={request.options.maxLength ?? 120}
              onChange={(event) => onValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") { event.preventDefault(); if (canConfirm) onClose(true, value); }
              }}
            />
          </label>
        )}
        <div className="app-dialog-actions">
          {request.kind !== "notify" && (
            <button type="button" className="app-dialog-cancel" onClick={() => onClose(false, value)}>
              {request.options.cancelLabel ?? "Cancel"}
            </button>
          )}
          <button
            ref={confirmRef}
            type="button"
            className={`app-dialog-confirm${tone === "danger" ? " danger" : ""}`}
            disabled={!canConfirm}
            onClick={() => onClose(true, value)}
          >
            {request.kind === "notify"
              ? request.options.dismissLabel ?? "OK"
              : request.options.confirmLabel ?? "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
